/* global APPCUES_ENV */

import apiFactory from '@appcues/flow-builder-api-client';
import { domain } from 'utils/opscues';
import HTTPError from 'errors/HTTPError';

const basePath = `https://${domain({
  app: 'flow-builder-api',
  env: APPCUES_ENV,
  facet: 'main',
})}`;

const customerApiPath = `https://${domain({
  app: 'customer-api',
  env: APPCUES_ENV,
  facet: 'main',
})}`;

const fetchCustomerApi = (token, accountId, path, method = 'GET', body) =>
  fetch(`${customerApiPath}/v1/accounts/${accountId}/${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-type': 'application/json',
    },
    ...(body && { body: JSON.stringify(body) }),
  })
    .then(async response => {
      if (response.ok) {
        return response.json();
      }

      const { error: errMsg } = await response.json();
      throw new HTTPError(response, errMsg);
    })
    .then(data => ({ data }));

const getRule = (token, accountId, id) =>
  fetchCustomerApi(token, accountId, `rules/${id}`);

const getSegmentMembership = (token, accountId, id) =>
  fetchCustomerApi(token, accountId, `segment_memberships/${id}`);

const getAllSegmentMemberships = (token, accountId) =>
  fetchCustomerApi(token, accountId, `segment_memberships`);

const getAllCustomEvents = (token, accountId) =>
  fetchCustomerApi(token, accountId, `custom_events`);

const createChart = (token, accountId, chart) =>
  fetchCustomerApi(token, accountId, `charts`, 'POST', chart);

const getAllCharts = (token, accountId) =>
  fetchCustomerApi(token, accountId, `charts`);

const getChart = (token, accountId, chartId) =>
  fetchCustomerApi(token, accountId, `charts/${chartId}`);

const updateChart = (token, accountId, chartId, delta) =>
  fetchCustomerApi(token, accountId, `charts/${chartId}`, 'PATCH', delta);

const deleteChart = (token, accountId, chartId) =>
  fetchCustomerApi(token, accountId, `charts/${chartId}`, 'DELETE');

const getAllSegments = (token, accountId) =>
  fetchCustomerApi(token, accountId, `segments`, 'GET');

const updateSegment = (token, accountId, id, delta) =>
  fetchCustomerApi(token, accountId, `segments/${id}`, 'PATCH', delta);

const deleteSegment = (token, accountId, id) =>
  fetchCustomerApi(token, accountId, `segments/${id}`, 'DELETE');

const createSegment = (token, accountId, segment) =>
  fetchCustomerApi(token, accountId, `segments`, 'POST', segment);

const getAllGoals = (token, accountId) =>
  fetchCustomerApi(token, accountId, `goals`, 'GET');

const updateGoal = (token, accountId, id, delta) =>
  fetchCustomerApi(token, accountId, `goals/${id}`, 'PATCH', delta);

const deleteGoal = (token, accountId, id) =>
  fetchCustomerApi(token, accountId, `goals/${id}`, 'DELETE');

const createGoal = (token, accountId, goal) =>
  fetchCustomerApi(token, accountId, `goals`, 'POST', goal);

const getAllPublishedRules = (token, accountId) =>
  fetchCustomerApi(token, accountId, `rules?sorted=true`, 'GET');

const getAllKeys = (token, accountId) =>
  fetchCustomerApi(token, accountId, `api_keys`, 'GET');

const createKey = (token, accountId, apiKey) =>
  fetchCustomerApi(token, accountId, `api_keys`, 'POST', apiKey);

const getAccountGates = (token, accountId) =>
  fetchCustomerApi(token, accountId, 'gates', 'GET');

const revokeKey = (token, accountId, key) =>
  fetchCustomerApi(token, accountId, `api_keys/${key}/revoke`, 'POST');

const activateKey = (token, accountId, key) =>
  fetchCustomerApi(token, accountId, `api_keys/${key}/activate`, 'POST');

const deleteKey = (token, accountId, key) =>
  fetchCustomerApi(token, accountId, `api_keys/${key}`, 'DELETE');

const getAllProfileAttributeLabels = (token, accountId) =>
  fetchCustomerApi(token, accountId, 'profile_attributes/labels', 'GET');

const updateProfileAttributeLabel = (token, accountId, name, delta) =>
  fetchCustomerApi(
    token,
    accountId,
    `profile_attributes/labels/${name}`,
    'PATCH',
    delta
  );

/**
 * As of 2019-10-23, the API client doesn't deal directly with the auth client.
 * So, to keep the code simple, we're injecting a patched version that handles authentication
 * inside of each call. Any additional arguments well be passed through to the original method.
 *
 * @param auth - an instance of an auth client
 *
 * @return object - contains all methods of the original client, partially applied with the JWT and account id
 *
 *  *
 * Example:
 *   given a method signature of
 *      getAllStepGroups(jwt, accountId, flowId),
 *
 *   the returned method looks like
 *      getAllStepGroups(flowId)
 */

export default function createApiClient(auth) {
  /**
   * As of 2020-06, the flow-builder-api-client is to be considered deprecated.
   * As such, we must add new & missing customer-api methods here in the
   * client wrapper
   */

  const client = {
    ...apiFactory({ basePath }),
    getRule,
    getSegmentMembership,
    getAllSegmentMemberships,
    getAllCustomEvents,
    createChart,
    getAllCharts,
    getChart,
    updateChart,
    deleteChart,
    getAllSegments,
    updateSegment,
    deleteSegment,
    createSegment,
    getAllGoals,
    updateGoal,
    deleteGoal,
    createGoal,
    getAllPublishedRules,
    getAllKeys,
    createKey,
    getAccountGates,
    revokeKey,
    activateKey,
    deleteKey,
    getAllProfileAttributeLabels,
    updateProfileAttributeLabel,
  };

  return Object.keys(client).reduce((acc, key) => {
    acc[key] = async (...args) => {
      const token = await auth.getToken();
      const id = Number.parseInt(await auth.getAccountId(), 10);
      return client[key](token, id, ...args);
    };
    return acc;
  }, {});
}
